import { css } from '@emotion/react';
import { Button, Icon, Modal, Stack } from '@inflearn/ds-react';
import { faCircleCheck } from '@inflearn/pro-solid-svg-icons';

import { MemoizedAgreementItem } from './AgreementItem';
import { TermSpoiler } from './TermSpoiler';
import type { AgreementValueType } from './useAgreementMap';
import { AGREEMENT_VALUES } from './useAgreementMap';
import { useAgreementMap } from './useAgreementMap';
import type { FindInflearnMarketingConfirmRequiredCoursesResponse } from '../../@types/schema';
import { EVENT_ID } from '../../constants/MODAL_EVENT_ID';
import useCourseContractHistoriesInflearnMarketingConfirmCreateMutation from '../../hooks/mutations/useCourseContractHistoriesInflearnMarketingConfirmCreateMutation';
import useCourseContractHistoriesInflearnMarketingConfirmRequiredQuery from '../../hooks/queires/useCourseContractHistoriesInflearnMarketingConfirmRequiredQuery';
import useCourseContractsInflearnMarketingContentsTermQuery from '../../hooks/queires/useCourseContractsInflearnMarketingContentsTermQuery';
import { useForAntManModalDisclosure } from '../../hooks/useForAntManModalDisclosure';

type Courses = FindInflearnMarketingConfirmRequiredCoursesResponse['data']['items'];

// NOTE: 지공자 온라인 강의 마케팅 모달은 두 곳에 존재합니다. ( for-ant-man / app-shell )
export const InstructorMarketingModal = () => {
  const [isOpened, handler] = useForAntManModalDisclosure({
    eventId: EVENT_ID.MARKETING_AGREEMENT_MODAL,
  });

  const { data: coursesData } = useCourseContractHistoriesInflearnMarketingConfirmRequiredQuery({
    onSuccess: ({ data }) => {
      if (data.items.length > 0) {
        handler.open();
      }
    },
  });

  const { data: termData } = useCourseContractsInflearnMarketingContentsTermQuery({
    enabled: (coursesData?.items ?? []).length > 0,
  });

  const { mutate: submitAgreement } =
    useCourseContractHistoriesInflearnMarketingConfirmCreateMutation();

  return (
    <InstructorMarkertingModalTemplate
      isOpened={isOpened}
      onClose={() => handler.close()}
      termHtml={termData?.content ?? ''}
      courses={coursesData?.items ?? []}
      handleSubmit={(selectedMap) => {
        const formattedMap = Array.from(selectedMap.entries()).map(([key, value]) => ({
          courseId: key,
          isAgreed: value === AGREEMENT_VALUES.AGREE,
        }));

        submitAgreement({ items: formattedMap });

        handler.close();
      }}
    />
  );
};

interface InstructorMarkertingModalTemplateProps {
  isOpened: boolean;
  onClose: () => void;
  termHtml: string;
  courses: Courses;
  handleSubmit: (selectedMap: Map<number, AgreementValueType>) => void;
}

export const InstructorMarkertingModalTemplate = ({
  isOpened,
  onClose,
  termHtml,
  courses,
  handleSubmit,
}: InstructorMarkertingModalTemplateProps) => {
  const { selectedMap, handleChangeAgreement, isAllSelected, setAllAgreements, isAllAgreed } =
    useAgreementMap(courses);

  // 모두 동의하기 버튼 클릭 시 실행할 함수
  const handleClickAllAgreeWithAction = () => {
    if (isAllAgreed) {
      return;
    }

    setAllAgreements(AGREEMENT_VALUES.AGREE);
  };

  return (
    <Modal.Root opened={isOpened} onClose={onClose} closeOnClickOutside={false} size="md">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>서비스 약관 변경 안내</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <TermSpoiler termHtml={termHtml} />
          <Stack spacing={8} mt={10}>
            {courses.map((course) => (
              <MemoizedAgreementItem
                key={course.id}
                id={course.id}
                leftText={course.title}
                onChange={handleChangeAgreement}
                selectedValue={selectedMap.get(course.id)}
              />
            ))}
          </Stack>
        </Modal.Body>
        <Modal.Footer
          align="vertical"
          styleWrapper={css`
            display: block !important;
          `}>
          <Button
            leftIcon={
              <Icon icon={faCircleCheck} size="md" color={isAllAgreed ? 'infgreen.7' : 'gray.5'} />
            }
            variant="default"
            color="infgreen"
            size="md"
            radius="md"
            onClick={handleClickAllAgreeWithAction}>
            모두 동의하기
          </Button>
          <Button
            disabled={!isAllSelected}
            variant="filled"
            color="infgreen"
            size="md"
            onClick={() => handleSubmit(selectedMap)}
            radius="md">
            제출하기
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
